import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, doc,
  increment, serverTimestamp, query, where, orderBy, limit, getDocs, Timestamp, startAfter,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { authService } from '../services/authService';
import BarcodeScanner from './BarcodeScanner';
import Paginacion from './common/Paginacion';
import { buscarProductosPorTexto } from '../utils/buscarProductos';
import { calcularKpisAlmacen } from '../utils/almacenKpis';
import { trackEvent } from '../lib/analytics';

const PAGE_SIZE = 25; // estilo StelOrder — igual que PAGE_SIZE_TODAS en OrdenesView.js

// ── Constantes ────────────────────────────────────────────────────────────────
// Firestore usa la MISMA estructura de campos que StelOrder (name, reference,
// sales-price, real-stock, inactive...) tanto para lo importado como para lo
// creado a mano aquí — solo `origen` y `marca` son propios de la app (StelOrder
// no los tiene). La categoría no se modela — en StelOrder es un recurso propio
// (product-category-id/path) fuera de alcance replicar.
const STOCK_STATUS = (stock, min) => {
  if (stock <= 0) return { label: 'Sin stock', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
  if (stock <= min) return { label: 'Bajo', cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
  return { label: 'OK', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' };
};

const EMPTY_PRODUCT = {
  name: '', reference: '', barcode: '', marca: '',
  description: '', 'purchase-price': '', 'sales-price': '',
  'real-stock': 0, 'minimum-stock': 2, location: '', inactive: false,
};

const TIPO_MOV_CFG = {
  entrada:    { label: 'Entrada', cls: 'text-green-700 bg-green-50',  symbol: '+' },
  salida:     { label: 'Salida',  cls: 'text-red-700 bg-red-50',      symbol: '-' },
  ajuste:     { label: 'Ajuste',  cls: 'text-blue-700 bg-blue-50',    symbol: '→' },
  uso_orden:  { label: 'Usado',   cls: 'text-orange-700 bg-orange-50', symbol: '-' },
};

// Traduce el filtro de periodo de "Movimientos" a un rango [desde, hasta) de
// Date — mismo criterio que rangoFechas() en OrdenesView.js, adaptado a
// Timestamp de Firestore en vez de string 'YYYY-MM-DD' (movimientos_stock.fecha
// es un serverTimestamp). null = sin acotar ("Todos").
function rangoFechasMov(filtro, desde, hasta) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  if (filtro === 'hoy') {
    const fin = new Date(hoy); fin.setDate(fin.getDate() + 1);
    return { desde: hoy, hasta: fin };
  }
  if (filtro === 'semana') {
    const lunes = new Date(hoy); lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7));
    const fin = new Date(lunes); fin.setDate(lunes.getDate() + 7);
    return { desde: lunes, hasta: fin };
  }
  if (filtro === 'mes') {
    return { desde: new Date(hoy.getFullYear(), hoy.getMonth(), 1), hasta: new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1) };
  }
  if (filtro === 'anio') {
    return { desde: new Date(hoy.getFullYear(), 0, 1), hasta: new Date(hoy.getFullYear() + 1, 0, 1) };
  }
  if (filtro === 'rango' && desde && hasta) {
    const d = new Date(desde + 'T00:00:00');
    const h = new Date(hasta + 'T00:00:00'); h.setDate(h.getDate() + 1);
    return { desde: d, hasta: h };
  }
  return null;
}

function fmtFechaMov(fecha) {
  if (!fecha) return '—';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Cabecera de columna ordenable — mismo patrón que TituloOrdenable en OrdenesView.js
function TituloOrdenable({ label, campo, sort, onSort }) {
  const activo = sort.campo === campo;
  return (
    <button onClick={() => onSort(campo)} className="flex items-center gap-1 hover:text-gray-800">
      {label}
      <span className={`text-[9px] ${activo ? 'text-gray-700' : 'text-gray-300'}`}>
        {activo && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </button>
  );
}

// ── Solicitudes de material (falta material al cerrar visita) ─────────────────
const SOLICITUD_ESTADO = {
  pendiente:  { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700' },
  pedido:     { label: 'Pedido',    cls: 'bg-blue-100 text-blue-700' },
  recibido:   { label: 'Recibido',  cls: 'bg-green-100 text-green-700' },
  entregado:  { label: 'Entregado', cls: 'bg-gray-100 text-gray-500' },
  anulada:    { label: 'Anulada',   cls: 'bg-gray-100 text-gray-400' },
};

function SolicitudesMaterial() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verTodas, setVerTodas] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await fetch('/.netlify/functions/getSolicitudesMaterial?limit=300', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      setSolicitudes(d.success ? (d.solicitudes || []) : []);
    } catch { setSolicitudes([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const accion = async (sol, acc) => {
    if (busyId) return;
    const sumarStock = acc === 'recibir' && (sol.items || []).some(it => it.productoId) &&
      window.confirm('¿Sumar al stock del almacén las unidades catalogadas de esta solicitud?');
    setBusyId(sol.id);
    try {
      const r = await fetch('/.netlify/functions/manageSolicitudMaterial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id: sol.id, accion: acc, sumarStock }),
      });
      const d = await r.json();
      if (!d.success) alert('Error: ' + (d.error || 'desconocido'));
      // Suma de stock fallida en el servidor sin bloquear la transición de
      // estado — queda en PostHog y, a diferencia del técnico, sí se avisa:
      // oficina pidió la suma a propósito (confirm() de arriba) y puede
      // querer reintentarla o ajustar el stock a mano.
      if (Array.isArray(d.avisos)) {
        for (const aviso of d.avisos) trackEvent('error_app', { source: aviso.tipo, message: aviso.mensaje });
        if (d.avisos.length) alert(d.avisos.map(a => a.mensaje).join('\n'));
      }
      await cargar();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setBusyId(null); }
  };

  const activas = solicitudes.filter(s => !['entregado', 'anulada'].includes(s.estado));
  const lista = verTodas ? solicitudes : activas;

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Cargando solicitudes...</div>;
  if (solicitudes.length === 0) return <div className="text-center py-12 text-gray-400 text-sm">Sin solicitudes de material registradas</div>;

  const NEXT = { pendiente: ['pedir', 'anular'], pedido: ['recibir', 'anular'], recibido: ['entregar'] };
  const ACC_LABEL = { pedir: 'Marcar pedido', recibir: 'Recibir', entregar: 'Entregar al técnico', anular: 'Anular' };

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="bg-amber-50 border-b border-amber-100 px-5 py-3 flex items-center gap-2">
        <span className="text-base">🧰</span>
        <h3 className="font-bold text-sm text-amber-800">Solicitudes de material</h3>
        {activas.length > 0 && (
          <span className="text-[10px] bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">{activas.length} activa{activas.length !== 1 ? 's' : ''}</span>
        )}
        <button onClick={() => setVerTodas(v => !v)} className="ml-auto text-xs text-amber-600 hover:text-amber-800 font-medium">
          {verTodas ? 'Solo activas' : 'Ver todas'}
        </button>
      </div>
      <div className="divide-y divide-gray-50">
        {lista.map(sol => {
          const est = SOLICITUD_ESTADO[sol.estado] || SOLICITUD_ESTADO.pendiente;
          return (
            <div key={sol.id} className="px-5 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${est.cls}`}>{est.label}</span>
                <span className="text-sm font-semibold text-gray-800">{sol.ordenRef || sol.ordenId}</span>
                {sol.clienteNombre && <span className="text-xs text-gray-400">· {sol.clienteNombre}</span>}
                {sol.tecnicoNombre && <span className="text-xs text-gray-400">· {sol.tecnicoNombre}</span>}
              </div>
              <ul className="mt-1 text-xs text-gray-600 space-y-0.5">
                {(sol.items || []).map((it, i) => (
                  <li key={i}>{it.productoId ? '📦' : '❓'} {it.cantidad || 1}× {it.nombre}{it.referencia ? ` (${it.referencia})` : ''}</li>
                ))}
              </ul>
              {sol.notas && <p className="text-xs text-gray-400 mt-1 italic">{sol.notas}</p>}
              {(NEXT[sol.estado] || []).length > 0 && (
                <div className="flex gap-2 mt-2">
                  {(NEXT[sol.estado] || []).map(acc => (
                    <button key={acc} onClick={() => accion(sol, acc)} disabled={busyId === sol.id}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                        acc === 'anular' ? 'bg-gray-50 hover:bg-gray-100 text-gray-500' : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                      }`}>
                      {busyId === sol.id ? '…' : ACC_LABEL[acc]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Formulario de producto (solo alta — editar es inline en la ficha) ─────────
function ProductoModal({ onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showScanner, setShowScanner] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    if (form.reference.trim()) {
      // Consulta puntual por referencia exacta — no hay un catálogo cargado
      // en memoria contra el que comprobar duplicados (ver ProductosTab).
      const dupSnap = await getDocs(query(collection(db, 'productos'), where('reference', '==', form.reference.trim()), limit(1)));
      if (!dupSnap.empty) {
        const dup = dupSnap.docs[0].data();
        setError(`La referencia "${form.reference}" ya existe en "${dup.name}"`);
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        'purchase-price': Number(form['purchase-price']) || 0,
        'sales-price':    Number(form['sales-price'])    || 0,
        'real-stock':     Number(form['real-stock'])     || 0,
        'minimum-stock':  Number(form['minimum-stock'])  || 0,
        'stock-enabled':  true,
      });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = 'text', placeholder = '') => (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="font-bold text-gray-900 flex-1">Nuevo producto</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {field('Nombre *', 'name', 'text', 'Ej: Sonda temperatura NTC')}
          <div className="grid grid-cols-2 gap-3">
            {field('Referencia (única)', 'reference', 'text', 'Ej: ARR-573120')}
            {field('Marca', 'marca', 'text', 'Ej: Ariston')}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">EAN / Código de barras</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.barcode}
                onChange={e => set('barcode', e.target.value)}
                placeholder="Ej: 8059892573120"
                className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300 font-mono"
              />
              <button type="button" onClick={() => setShowScanner(true)}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition flex items-center gap-1.5 flex-shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                Escanear
              </button>
            </div>
          </div>

          {showScanner && (
            <BarcodeScanner onScan={code => { set('barcode', code); setShowScanner(false); }} onClose={() => setShowScanner(false)} />
          )}

          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descripción</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Descripción detallada del producto..." rows={2}
              className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-300" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Precio coste (€)</label>
              <input type="number" min="0" step="0.01" value={form['purchase-price']} onChange={e => set('purchase-price', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Precio venta (€)</label>
              <input type="number" min="0" step="0.01" value={form['sales-price']} onChange={e => set('sales-price', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stock inicial</label>
              <input type="number" min="0" step="1" value={form['real-stock']} onChange={e => set('real-stock', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mínimo</label>
              <input type="number" min="0" step="1" value={form['minimum-stock']} onChange={e => set('minimum-stock', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ubicación</label>
              <input type="text" value={form.location} onChange={e => set('location', e.target.value)} placeholder="Ej: A-3"
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

          <button onClick={handleSave} disabled={saving}
            className="w-full py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-2xl transition-colors">
            {saving ? 'Guardando...' : 'Crear producto'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal ajuste de stock — con selector de producto cuando no llega
// preseleccionado (se abre desde la pestaña "Movimientos" en vez de desde
// una fila/ficha concreta) ─────────────────────────────────────────────────
function AjusteModal({ productoInicial, onClose, onAjuste }) {
  const [producto, setProducto] = useState(productoInicial || null);
  const [productos, setProductos] = useState([]);
  const [buscar,   setBuscar]   = useState('');
  const [tipo,     setTipo]     = useState('entrada');
  const [cantidad, setCantidad] = useState('');
  const [notas,    setNotas]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  // Los ajustes de stock solo aplican a productos creados en Almacén
  // (origen 'app') — el catálogo StelOrder es de solo consulta. Ese conjunto
  // es naturalmente pequeño (altas manuales, no las ~22.700 referencias de
  // StelOrder), así que aquí sí se carga entero de una vez, sin paginación.
  useEffect(() => {
    if (productoInicial) return;
    getDocs(query(collection(db, 'productos'), where('origen', '==', 'app')))
      .then(snap => setProductos(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setProductos([]));
  }, [productoInicial]);

  const handleConfirm = async () => {
    const qty = Number(cantidad);
    if (!qty || qty <= 0) { setError('Introduce una cantidad válida'); return; }
    if (tipo === 'salida' && qty > producto['real-stock']) { setError('No hay suficiente stock'); return; }
    setSaving(true);
    setError('');
    try {
      await onAjuste(producto, { tipo, cantidad: qty, notas });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deltaText = tipo === 'entrada' ? `+${cantidad || 0}` : tipo === 'salida' ? `-${cantidad || 0}` : `→ ${cantidad || 0}`;
  const newStock  = !producto ? 0 : tipo === 'entrada'
    ? producto['real-stock'] + (Number(cantidad) || 0)
    : tipo === 'salida'
    ? producto['real-stock'] - (Number(cantidad) || 0)
    : Number(cantidad) || producto['real-stock'];

  const candidatos = productos.filter(p =>
    !buscar || [p.name, p.reference, p.barcode].some(v => v?.toLowerCase().includes(buscar.toLowerCase()))
  ).slice(0, 30);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="font-bold text-gray-900 flex-1">Registrar movimiento</h2>
          {producto && <p className="text-sm text-gray-500 truncate max-w-[180px]">{producto.name}</p>}
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!producto ? (
          <div className="px-5 py-4 space-y-3">
            <input
              type="text" value={buscar} onChange={e => setBuscar(e.target.value)} autoFocus
              placeholder="Buscar producto por nombre, referencia o EAN..."
              className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
              {candidatos.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Sin resultados</p>}
              {candidatos.map(p => (
                <button key={p.id} onClick={() => setProducto(p)} className="w-full text-left py-2.5 hover:bg-gray-50 transition-colors">
                  <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.reference || '—'} · stock {p['real-stock'] ?? 0}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: 'entrada', label: 'Entrada', emoji: '📥', cls: 'border-green-400 bg-green-50 text-green-800' },
                { k: 'salida',  label: 'Salida',  emoji: '📤', cls: 'border-red-400 bg-red-50 text-red-800' },
                { k: 'ajuste',  label: 'Ajustar', emoji: '⚖️', cls: 'border-blue-400 bg-blue-50 text-blue-800' },
              ].map(t => (
                <button key={t.k} onClick={() => setTipo(t.k)}
                  className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${tipo === t.k ? t.cls : 'border-gray-200 bg-white text-gray-600'}`}>
                  <span className="text-xl">{t.emoji}</span>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 text-sm">
              <span className="text-gray-500">Stock actual</span>
              <div className="flex items-center gap-3">
                <span className="font-bold text-gray-800">{producto['real-stock']}</span>
                {cantidad && <span className="text-gray-400">→</span>}
                {cantidad && <span className={`font-bold ${newStock < 0 ? 'text-red-600' : newStock <= producto['minimum-stock'] ? 'text-amber-600' : 'text-green-700'}`}>{newStock}</span>}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {tipo === 'ajuste' ? 'Nuevo stock total' : 'Cantidad'}
              </label>
              <input type="number" min="1" step="1" value={cantidad} onChange={e => setCantidad(e.target.value)} placeholder="0" autoFocus
                className="w-full text-lg font-bold text-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Motivo / notas (opcional)</label>
              <input type="text" value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Ej: Compra proveedor X, usado en orden ZAP-0001..."
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

            <button onClick={handleConfirm} disabled={saving || !cantidad}
              className="w-full py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-2xl transition-colors">
              {saving ? 'Guardando...' : `Confirmar ${tipo} ${deltaText}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Ficha de producto — lista compacta + panel, estilo OrdenDetallePanel ──────
function ProductoDetallePanel({ producto, lista, onSeleccionar, onClose, onUpdate, onAjustar }) {
  const [tab,     setTab]     = useState('info');
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [movs,    setMovs]    = useState([]);
  const [loadingMovs, setLoadingMovs] = useState(true);

  // Al cambiar de producto (clic en otro de la lista compacta) hay que
  // resincronizar el estado local — mismo motivo que el useEffect equivalente
  // en OrdenDetallePanel (OrdenesView.js): sin esto se seguía mostrando el
  // producto anterior al no desmontarse el panel.
  useEffect(() => {
    setTab('info');
    setEditing(false);
    setError('');
  }, [producto.id]);

  useEffect(() => {
    setLoadingMovs(true);
    getDocs(query(collection(db, 'movimientos_stock'), where('productoId', '==', producto.id), limit(200)))
      .then(snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const fa = a.fecha?.toMillis ? a.fecha.toMillis() : new Date(a.fecha || 0).getTime();
          const fb = b.fecha?.toMillis ? b.fecha.toMillis() : new Date(b.fecha || 0).getTime();
          return fb - fa;
        });
        setMovs(rows.slice(0, 50));
      })
      .catch(() => setMovs([]))
      .finally(() => setLoadingMovs(false));
  }, [producto.id]);

  const startEdit = () => {
    setForm({
      name: producto.name || '', reference: producto.reference || '', marca: producto.marca || '',
      barcode: producto.barcode || '', description: producto.description || '',
      'purchase-price': producto['purchase-price'] ?? '', 'sales-price': producto['sales-price'] ?? '',
      'minimum-stock': producto['minimum-stock'] ?? 0, location: producto.location || '',
      inactive: producto.inactive === true,
    });
    setEditing(true);
    setError('');
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true); setError('');
    try {
      const patch = {
        ...form,
        'purchase-price': Number(form['purchase-price']) || 0,
        'sales-price':    Number(form['sales-price'])    || 0,
        'minimum-stock':  Number(form['minimum-stock'])  || 0,
      };
      await updateDoc(doc(db, 'productos', producto.id), patch);
      onUpdate({ ...producto, ...patch });
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const status = STOCK_STATUS(producto['real-stock'] || 0, producto['minimum-stock'] || 0);
  const margen = (Number(producto['sales-price']) || 0) - (Number(producto['purchase-price']) || 0);
  const margenPct = producto['sales-price'] > 0 ? Math.round((margen / producto['sales-price']) * 100) : null;

  const field = (label, key, type = 'text') => (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300" />
    </div>
  );

  return (
    <div className="flex gap-4 h-[calc(100vh-160px)]">
      {/* Lista compacta izquierda */}
      <div className="w-80 flex-shrink-0 hidden md:flex flex-col bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center gap-2 flex-shrink-0">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600 hover:text-gray-800 flex items-center gap-1">← Lista</button>
          <span className="text-xs text-gray-400 ml-auto">{lista.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
          {lista.map(p => {
            const isSelected = p.id === producto.id;
            const st = STOCK_STATUS(p['real-stock'] || 0, p['minimum-stock'] || 0);
            return (
              <button key={p.id} onClick={() => onSeleccionar(p)}
                className={`w-full text-left px-4 py-3 transition-colors ${isSelected ? 'bg-red-50 border-l-[3px] border-red-500' : 'hover:bg-gray-50'}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${st.cls}`}>{p['real-stock'] ?? 0} ud.</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                <p className="text-xs text-gray-400 truncate">{p.reference || '—'}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Panel de detalle */}
      <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border flex flex-col overflow-hidden max-h-[calc(100vh-160px)]">
        <div className="px-5 py-4 border-b flex-shrink-0 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button onClick={onClose} className="flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800 -ml-1 px-1.5 py-1 rounded-lg hover:bg-gray-100">
              ← Volver
            </button>
            <div className="flex items-center gap-2">
              {!editing && producto.origen !== 'stelorder' && (
                <button onClick={() => onAjustar(producto)} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">
                  ⚖️ Ajustar stock
                </button>
              )}
              {!editing && producto.origen !== 'stelorder' && (
                <button onClick={startEdit} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">
                  ✏️ Editar
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-bold text-gray-900 text-base">{producto.name}</p>
            {producto.reference && <span className="text-xs text-gray-400 font-mono bg-gray-50 px-1.5 py-0.5 rounded">{producto.reference}</span>}
            {producto.origen === 'stelorder' && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Catálogo StelOrder — solo consulta</span>
            )}
          </div>

          {!editing ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <CampoBox label="Marca" value={producto.marca || '—'} />
                <CampoBox label="Ubicación" value={producto.location || '—'} />
              </div>
              <div className="space-y-2">
                <CampoBox label="Stock">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${status.cls}`}>{producto['real-stock'] ?? 0} ud.</span>
                    {producto['minimum-stock'] > 0 && <span className="text-xs text-gray-400">mín. {producto['minimum-stock']}</span>}
                  </div>
                </CampoBox>
                <CampoBox label="Precio compra / venta">
                  <p className="text-sm text-gray-800">€{Number(producto['purchase-price'] || 0).toFixed(2)} / €{Number(producto['sales-price'] || 0).toFixed(2)}</p>
                </CampoBox>
                <CampoBox label="Margen">
                  <p className={`text-sm font-semibold ${margen >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    €{margen.toFixed(2)}{margenPct !== null && ` (${margenPct}%)`}
                  </p>
                </CampoBox>
              </div>
            </div>
          ) : (
            <div className="space-y-3 bg-gray-50 rounded-xl p-3">
              <div className="grid grid-cols-2 gap-3">
                {field('Nombre', 'name')}
                {field('Referencia', 'reference')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field('Marca', 'marca')}
                {field('EAN', 'barcode')}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descripción</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-300" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {field('Precio coste (€)', 'purchase-price', 'number')}
                {field('Precio venta (€)', 'sales-price', 'number')}
                {field('Stock mínimo', 'minimum-stock', 'number')}
              </div>
              {/* El stock actual NO se edita aquí — cambia solo vía "Ajustar
                  stock", para que quede siempre un movimiento registrado. */}
              {field('Ubicación', 'location')}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={!form.inactive} onChange={e => setForm(f => ({ ...f, inactive: !e.target.checked }))} />
                Producto activo
              </label>
              {error && <p className="text-sm text-red-600 bg-red-100 rounded-xl px-3 py-2">{error}</p>}
              <div className="flex gap-2">
                <button onClick={handleSaveEdit} disabled={saving}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-colors">
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
                <button onClick={() => setEditing(false)} className="px-4 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-100 rounded-xl">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 border-b flex-shrink-0 flex gap-4">
          {[{ id: 'info', label: 'Información' }, { id: 'historico', label: 'Histórico' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {tab === 'info' && (
            <div className="space-y-3 text-sm text-gray-700">
              {producto.description
                ? <p className="whitespace-pre-wrap">{producto.description}</p>
                : <p className="text-gray-400">Sin descripción</p>}
              {producto.barcode && <p className="text-xs text-gray-400 font-mono">EAN: {producto.barcode}</p>}
              <p className="text-xs text-gray-400">
                {producto.inactive === true ? 'Producto de baja' : 'Producto activo'}
              </p>
            </div>
          )}
          {tab === 'historico' && (
            <>
              {loadingMovs && <p className="text-sm text-gray-400 text-center py-6">Cargando...</p>}
              {!loadingMovs && movs.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Sin movimientos registrados</p>}
              {!loadingMovs && movs.length > 0 && (
                <div className="space-y-2">
                  {movs.map(m => {
                    const cfg = TIPO_MOV_CFG[m.tipo] || TIPO_MOV_CFG.ajuste;
                    return (
                      <div key={m.id} className="flex items-center gap-3 py-2.5 border-b border-gray-50">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800">
                            {cfg.symbol}{m.cantidad} unidades
                            <span className="text-gray-400 font-normal"> · {m.stockAnterior} → {m.stockNuevo}</span>
                          </p>
                          {m.notas && <p className="text-xs text-gray-500 truncate">{m.notas}</p>}
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">{fmtFechaMov(m.fecha)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CampoBox({ label, value, children }) {
  return (
    <div className="bg-gray-50 rounded-xl px-3 py-2">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
      {children || <p className="text-sm text-gray-800">{value}</p>}
    </div>
  );
}

// ── Pestaña: Productos ─────────────────────────────────────────────────────────
const BROWSE_PAGE = 25;

function ProductosTab({ onDetalle, onAjustar, soloAlertasInicial, onListaChange }) {
  const [busqueda, setBusqueda] = useState('');
  const [modalProd, setModalProd] = useState(false);
  // Inicializador perezoso: solo importa el valor que traía el drill-down de
  // Analítica en el momento en que esta pestaña se monta (ProductosTab se
  // desmonta al cambiar de pestaña, así que esto no queda "pegado" después).
  const [soloAlertas, setSoloAlertas] = useState(() => !!soloAlertasInicial);
  const [showScanner, setShowScanner] = useState(false);
  const [sort, setSort] = useState({ campo: null, dir: 'asc' });
  const [page, setPage] = useState(1);

  // Modo "explorar" (por defecto): paginación real contra Firestore con
  // cursor, nunca el catálogo entero (~22.700 productos reales de
  // StelOrder) — "Cargar más" trae la siguiente página bajo demanda.
  const [browseItems, setBrowseItems] = useState([]);
  const [browseCursor, setBrowseCursor] = useState(null);
  const [browseHasMore, setBrowseHasMore] = useState(true);
  const [loadingBrowse, setLoadingBrowse] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const cargarBrowse = useCallback(async (cursor = null) => {
    if (cursor) setLoadingMore(true); else setLoadingBrowse(true);
    try {
      const base = [collection(db, 'productos'), orderBy('name'), limit(BROWSE_PAGE)];
      const q = cursor ? query(...base, startAfter(cursor)) : query(...base);
      const snap = await getDocs(q);
      const nuevos = snap.docs.map(d => { const p = d.data(); return { id: d.id, ...p, origen: p.origen || 'app' }; });
      setBrowseItems(prev => (cursor ? [...prev, ...nuevos] : nuevos));
      setBrowseCursor(snap.docs[snap.docs.length - 1] || null);
      setBrowseHasMore(snap.docs.length === BROWSE_PAGE);
    } catch {
      if (!cursor) setBrowseItems([]);
    } finally {
      setLoadingBrowse(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { cargarBrowse(); }, [cargarBrowse]);

  // Modo "stock bajo": productos CON mínimo configurado — un universo mucho
  // más chico que el catálogo entero (la inmensa mayoría de StelOrder no
  // lleva mínimo puesto), ver almacenKpis.js. Se carga una vez, no hace
  // falta paginar.
  const [alertaItems, setAlertaItems] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(true);
  useEffect(() => {
    getDocs(query(collection(db, 'productos'), where('minimum-stock', '>', 0), where('inactive', '==', false), limit(2000)))
      .then(snap => setAlertaItems(
        snap.docs.map(d => { const p = d.data(); return { id: d.id, ...p, origen: p.origen || 'app' }; })
          .filter(p => (p['real-stock'] || 0) <= (p['minimum-stock'] || 0))
      ))
      .catch(() => setAlertaItems([]))
      .finally(() => setLoadingAlertas(false));
  }, []);

  // Modo "búsqueda": bajo demanda contra Firestore, ver buscarProductos.js.
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  useEffect(() => {
    if (busqueda.trim().length < 2) { setSearchResults([]); return; }
    let vivo = true;
    setLoadingSearch(true);
    const t = setTimeout(() => {
      buscarProductosPorTexto(busqueda, { max: 30 }).then(rows => {
        if (vivo) setSearchResults(rows.map(p => ({ ...p, origen: p.origen || 'app' })));
      }).catch(() => { if (vivo) setSearchResults([]); }).finally(() => { if (vivo) setLoadingSearch(false); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busqueda]);

  const modo = busqueda.trim().length >= 2 ? 'busqueda' : soloAlertas ? 'alertas' : 'browse';
  const base = modo === 'busqueda' ? searchResults : modo === 'alertas' ? alertaItems : browseItems;
  const loadingBase = modo === 'busqueda' ? loadingSearch : modo === 'alertas' ? loadingAlertas : loadingBrowse;

  const toggleSort = (campo) => setSort(s => (s.campo === campo ? { campo, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' }));

  const filtrados = useMemo(() => {
    if (!sort.campo) return modo === 'browse' ? base : [...base].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
    return [...base].sort((a, b) => {
      const av = sort.campo === 'name' ? (a.name || '') : sort.campo === 'stock' ? (a['real-stock'] || 0)
        : sort.campo === 'sales-price' ? (a['sales-price'] || 0) : ((a['sales-price'] || 0) - (a['purchase-price'] || 0));
      const bv = sort.campo === 'name' ? (b.name || '') : sort.campo === 'stock' ? (b['real-stock'] || 0)
        : sort.campo === 'sales-price' ? (b['sales-price'] || 0) : ((b['sales-price'] || 0) - (b['purchase-price'] || 0));
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv, 'es') : bv.localeCompare(av, 'es');
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
  }, [base, sort, modo]);

  useEffect(() => { setPage(1); }, [busqueda, soloAlertas, sort]);
  useEffect(() => { onListaChange?.(filtrados); }, [filtrados, onListaChange]);

  // Paginación cliente solo en 'busqueda'/'alertas' (conjuntos ya acotados
  // del lado del servidor); 'browse' usa "Cargar más" con cursor.
  const usaPaginacionCliente = modo !== 'browse';
  const totalPages = usaPaginacionCliente ? Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE)) : 1;
  const pageClamped = Math.min(page, totalPages);
  const visibles = usaPaginacionCliente ? filtrados.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE) : filtrados;

  const alertCount = alertaItems.length;

  const handleSaveProducto = async (data) => {
    const now = serverTimestamp();
    const nuevo = { ...data, origen: 'app', creadoEn: now, actualizadoEn: now };
    const ref = await addDoc(collection(db, 'productos'), nuevo);
    // Sin catálogo entero en memoria: se antepone a lo ya cargado y se
    // vuelve al modo "explorar" para que se vea de inmediato.
    setBrowseItems(prev => [{ id: ref.id, ...nuevo }, ...prev]);
    setBusqueda('');
    setSoloAlertas(false);
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      <div className="flex gap-2 flex-wrap items-center flex-shrink-0">
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, referencia, EAN..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300" />
        </div>
        <button onClick={() => setShowScanner(true)}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
          📷 Escanear
        </button>
        {alertCount > 0 && (
          <button onClick={() => { setSoloAlertas(v => !v); setBusqueda(''); }}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${soloAlertas ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
            ⚠️ {alertCount} con stock bajo
          </button>
        )}
        <button onClick={() => setModalProd(true)} className="ml-auto flex items-center gap-1.5 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors">
          + Nuevo producto
        </button>
      </div>

      {modalProd && <ProductoModal onClose={() => setModalProd(false)} onSave={handleSaveProducto} />}

      {showScanner && <BarcodeScanner onScan={code => { setBusqueda(code); setShowScanner(false); }} onClose={() => setShowScanner(false)} />}

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider select-none">
                  <TituloOrdenable label="Producto" campo="name" sort={sort} onSort={toggleSort} />
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Marca</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider select-none">
                  <TituloOrdenable label="Stock" campo="stock" sort={sort} onSort={toggleSort} />
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell select-none">
                  <TituloOrdenable label="P. venta" campo="sales-price" sort={sort} onSort={toggleSort} />
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell select-none">
                  <TituloOrdenable label="Margen" campo="margen" sort={sort} onSort={toggleSort} />
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingBase && visibles.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Cargando...</td></tr>
              )}
              {!loadingBase && visibles.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                  {modo === 'busqueda' ? 'Sin resultados para esta búsqueda' : modo === 'alertas' ? 'Ningún producto con stock bajo' : 'No hay productos en el almacén'}
                </td></tr>
              )}
              {visibles.map(p => {
                const status = STOCK_STATUS(p['real-stock'] || 0, p['minimum-stock'] || 0);
                const margen = (Number(p['sales-price']) || 0) - (Number(p['purchase-price']) || 0);
                return (
                  <tr key={p.id} onClick={() => onDetalle(p)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <p className="font-semibold text-gray-900">{p.name}</p>
                        {p.origen === 'stelorder' && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600" title="Catálogo StelOrder — solo consulta">StelOrder</span>
                        )}
                      </div>
                      {p.reference && <p className="text-xs text-gray-400 font-mono">{p.reference}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{p.marca || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${status.dot}`} />
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${status.cls}`}>{p['real-stock'] ?? 0} ud.</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-mono hidden lg:table-cell">{p['sales-price'] > 0 ? `€${Number(p['sales-price']).toFixed(2)}` : '—'}</td>
                    <td className="px-4 py-3 text-xs font-mono hidden lg:table-cell">
                      {p['sales-price'] > 0 || p['purchase-price'] > 0 ? <span className={margen >= 0 ? 'text-green-600' : 'text-red-600'}>€{margen.toFixed(2)}</span> : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {p.origen !== 'stelorder' && (
                        <button onClick={e => { e.stopPropagation(); onAjustar(p); }}
                          className="text-xs font-semibold px-2 py-1 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg">⚖️</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {modo === 'browse' && filtrados.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-50 flex-shrink-0">
            <span className="text-[11px] text-gray-400">{filtrados.length} cargados</span>
            {browseHasMore && (
              <button onClick={() => cargarBrowse(browseCursor)} disabled={loadingMore}
                className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50">
                {loadingMore ? 'Cargando…' : 'Cargar más'}
              </button>
            )}
          </div>
        )}
        {usaPaginacionCliente && filtrados.length > 0 && (
          <div className="flex items-center justify-between px-4 border-t border-gray-50 flex-shrink-0">
            <span className="text-[11px] text-gray-400">
              {(pageClamped - 1) * PAGE_SIZE + 1}–{Math.min(pageClamped * PAGE_SIZE, filtrados.length)} de {filtrados.length}
            </span>
            <Paginacion page={pageClamped} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pestaña: Movimientos ────────────────────────────────────────────────────────
const FILTROS_PERIODO_MOV = [
  { id: 'hoy',    label: 'Hoy' },
  { id: 'semana', label: 'Esta semana' },
  { id: 'mes',    label: 'Este mes' },
  { id: 'anio',   label: 'Este año' },
  { id: 'rango',  label: 'Rango…' },
  { id: '',       label: 'Todos' },
];

function MovimientosTab({ onRegistrar }) {
  const [filtroPeriodo, setFiltroPeriodo] = useState('mes');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [buscarProducto, setBuscarProducto] = useState('');
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (filtroPeriodo === 'rango' && (!fechaDesde || !fechaHasta)) return;
    setLoading(true);
    const rango = rangoFechasMov(filtroPeriodo, fechaDesde, fechaHasta);
    const q = rango
      ? query(collection(db, 'movimientos_stock'),
          where('fecha', '>=', Timestamp.fromDate(rango.desde)), where('fecha', '<', Timestamp.fromDate(rango.hasta)),
          orderBy('fecha', 'desc'), limit(1000))
      : query(collection(db, 'movimientos_stock'), orderBy('fecha', 'desc'), limit(500));
    getDocs(q)
      .then(snap => setMovimientos(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setMovimientos([]))
      .finally(() => setLoading(false));
  }, [filtroPeriodo, fechaDesde, fechaHasta]);

  const filtrados = useMemo(() => movimientos.filter(m => {
    const matchTipo = !filtroTipo || m.tipo === filtroTipo;
    const matchProd = !buscarProducto || (m.productoNombre || '').toLowerCase().includes(buscarProducto.toLowerCase());
    return matchTipo && matchProd;
  }), [movimientos, filtroTipo, buscarProducto]);

  useEffect(() => { setPage(1); }, [filtrados.length, filtroTipo, buscarProducto]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const visibles = filtrados.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE);

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      <div className="flex gap-2 flex-wrap items-center flex-shrink-0">
        <select value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)}
          className="text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300">
          {FILTROS_PERIODO_MOV.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        {filtroPeriodo === 'rango' && (
          <>
            <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
              className="text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            <span className="text-xs text-gray-400">→</span>
            <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
              className="text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
          </>
        )}
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          className="text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300">
          <option value="">Todos los tipos</option>
          {Object.entries(TIPO_MOV_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="text" value={buscarProducto} onChange={e => setBuscarProducto(e.target.value)} placeholder="Buscar producto..."
          className="text-sm bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex-1 min-w-[160px] focus:outline-none focus:ring-2 focus:ring-red-300" />
        <button onClick={() => onRegistrar()} className="ml-auto flex items-center gap-1.5 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors">
          + Registrar movimiento
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex justify-center py-16 text-sm text-gray-400">Cargando movimientos...</div>
        ) : (
          <>
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Producto</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Stock</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Notas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visibles.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Sin movimientos en este periodo</td></tr>
                  )}
                  {visibles.map(m => {
                    const cfg = TIPO_MOV_CFG[m.tipo] || TIPO_MOV_CFG.ajuste;
                    return (
                      <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs text-gray-500">{fmtFechaMov(m.fecha)}</td>
                        <td className="px-4 py-3 font-semibold text-gray-800">{m.productoNombre || '—'}</td>
                        <td className="px-4 py-3"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span></td>
                        <td className="px-4 py-3 font-mono text-xs">{cfg.symbol}{m.cantidad}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 hidden md:table-cell">{m.stockAnterior} → {m.stockNuevo}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 truncate max-w-[240px] hidden lg:table-cell">{m.notas || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtrados.length > 0 && (
              <div className="flex items-center justify-between px-4 border-t border-gray-50 flex-shrink-0">
                <span className="text-[11px] text-gray-400">
                  {(pageClamped - 1) * PAGE_SIZE + 1}–{Math.min(pageClamped * PAGE_SIZE, filtrados.length)} de {filtrados.length}
                </span>
                <Paginacion page={pageClamped} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Pestaña: Servicios — catálogo StelOrder, solo consulta ───────────────────────
// A diferencia de Productos, no hay alta/edición manual (no existe ese flujo
// hoy) ni stock — es una tabla más simple, solo de referencia.
function ServiciosTab({ servicios, loading }) {
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(1);

  const filtrados = useMemo(() => servicios.filter(s =>
    !busqueda || [s.name, s.reference, s.description].some(v => v?.toLowerCase().includes(busqueda.toLowerCase()))
  ).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })), [servicios, busqueda]);

  useEffect(() => { setPage(1); }, [busqueda]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const visibles = filtrados.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE);

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      <div className="flex gap-2 flex-wrap items-center flex-shrink-0">
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, referencia..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300" />
        </div>
        <span className="text-xs text-gray-400 bg-gray-50 px-3 py-2.5 rounded-xl">Catálogo StelOrder — solo consulta</span>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex justify-center py-16 text-sm text-gray-400">Cargando servicios...</div>
        ) : (
          <>
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Servicio</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Descripción</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Precio</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">IVA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visibles.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                      {servicios.length === 0 ? 'Sin servicios importados de StelOrder' : 'Sin resultados para esta búsqueda'}
                    </td></tr>
                  )}
                  {visibles.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{s.name}</p>
                        {s.reference && <p className="text-xs text-gray-400 font-mono">{s.reference}</p>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[280px] hidden md:table-cell">{s.description || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-700 font-mono">{s['sales-price'] > 0 ? `€${Number(s['sales-price']).toFixed(2)}` : '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{s['primary-tax-percentage'] != null ? `${s['primary-tax-percentage']}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtrados.length > 0 && (
              <div className="flex items-center justify-between px-4 border-t border-gray-50 flex-shrink-0">
                <span className="text-[11px] text-gray-400">
                  {(pageClamped - 1) * PAGE_SIZE + 1}–{Math.min(pageClamped * PAGE_SIZE, filtrados.length)} de {filtrados.length}
                </span>
                <Paginacion page={pageClamped} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Pestaña: Analítica ───────────────────────────────────────────────────────────
function KPICard({ icon, label, value, sub, colorBg, onClick }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`text-left bg-white rounded-2xl border shadow-sm p-4 transition-all ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${colorBg}`}>{icon}</div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs font-semibold text-gray-600 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </button>
  );
}

function AnaliticaTab({ movimientos, loadingMovimientos, onIrAProductos }) {
  // Dos consultas acotadas, no el catálogo completo (~22.700 productos,
  // casi todos sin stock gestionado en StelOrder) — ver almacenKpis.js.
  const [productosConStock, setProductosConStock]   = useState([]);
  const [productosConMinimo, setProductosConMinimo] = useState([]);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [consumo, setConsumo] = useState(null);
  const [loadingConsumo, setLoadingConsumo] = useState(true);

  useEffect(() => {
    setLoadingKpis(true);
    Promise.all([
      getDocs(query(collection(db, 'productos'), where('real-stock', '>', 0), limit(2000))),
      getDocs(query(collection(db, 'productos'), where('minimum-stock', '>', 0), where('inactive', '==', false), limit(2000))),
    ]).then(([snapStock, snapMinimo]) => {
      setProductosConStock(snapStock.docs.map(d => ({ id: d.id, ...d.data() })));
      setProductosConMinimo(snapMinimo.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(() => {}).finally(() => setLoadingKpis(false));
  }, []);

  // Consumo de materiales desde líneas de órdenes terminadas — más relevante
  // que el catálogo del almacén para saber qué se usa de verdad. Resumen
  // agregado en el backend (getConsumoMateriales.js): nunca se descargan las
  // órdenes completas ni el catálogo de productos para esto.
  useEffect(() => {
    setLoadingConsumo(true);
    fetch('/.netlify/functions/getConsumoMateriales', { headers: { ...authService.authHeader() } })
      .then(r => r.json()).then(data => setConsumo(data.success ? data : null))
      .catch(() => setConsumo(null)).finally(() => setLoadingConsumo(false));
  }, []);

  const kpis = useMemo(() => calcularKpisAlmacen(productosConStock, productosConMinimo, movimientos), [productosConStock, productosConMinimo, movimientos]);

  return (
    <div className="space-y-4 overflow-y-auto h-full">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={<span className="text-lg">💰</span>} colorBg="bg-green-100" label="Valor de inventario"
          value={loadingKpis ? '…' : `€${kpis.valorInventario.toFixed(0)}`} sub={loadingKpis ? '' : `coste €${kpis.valorCoste.toFixed(0)} · ${kpis.totalProductos} productos con stock`} />
        <KPICard icon={<span className="text-lg">⚠️</span>} colorBg="bg-amber-100" label="Stock bajo"
          value={loadingKpis ? '…' : kpis.stockBajo} sub="por debajo del mínimo" onClick={onIrAProductos} />
        <KPICard icon={<span className="text-lg">🚫</span>} colorBg="bg-red-100" label="Sin stock"
          value={loadingKpis ? '…' : kpis.sinStock} sub="con mínimo configurado" onClick={onIrAProductos} />
        <KPICard icon={<span className="text-lg">📦</span>} colorBg="bg-blue-100" label="Movimientos"
          value={loadingMovimientos ? '…' : kpis.movimientosPeriodo} sub={`${kpis.entradas} entradas · ${kpis.salidas} salidas`} />
      </div>

      {/* Consumo real de materiales en órdenes terminadas — más relevante
          para saber qué se usa de verdad que el catálogo del almacén, que
          en StelOrder son ~22.700 referencias en su mayoría sin relación
          con el trabajo diario. */}
      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="bg-gray-50 border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-bold text-sm text-gray-700">Materiales más usados (últimos 90 días, órdenes terminadas)</h3>
          {consumo && <span className="text-xs text-gray-400">{consumo.ordenesTerminadas} órdenes analizadas</span>}
        </div>
        <div className="divide-y divide-gray-50">
          {loadingConsumo && <p className="text-sm text-gray-400 text-center py-6">Cargando...</p>}
          {!loadingConsumo && (!consumo || consumo.topMateriales.length === 0) && (
            <p className="text-sm text-gray-400 text-center py-6">Sin materiales registrados en órdenes de este periodo</p>
          )}
          {!loadingConsumo && consumo?.topMateriales.map(m => (
            <div key={m.productoId || m.referencia || m.nombre} className="flex items-center justify-between px-5 py-2.5 text-sm">
              <div className="min-w-0">
                <p className="text-gray-700 truncate">{m.nombre}</p>
                {m.referencia && <p className="text-xs text-gray-400 font-mono">{m.referencia}</p>}
              </div>
              <span className="font-mono font-semibold text-gray-900 flex-shrink-0">{m.unidades} ud. · {m.apariciones} órdenes</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b px-5 py-3">
            <h3 className="font-bold text-sm text-gray-700">Sin stock</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {loadingKpis && <p className="text-sm text-gray-400 text-center py-6">Cargando...</p>}
            {!loadingKpis && kpis.sinStockLista.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Ningún producto con mínimo configurado está agotado</p>}
            {!loadingKpis && kpis.sinStockLista.slice(0, 8).map(p => (
              <div key={p.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                <span className="text-gray-700 truncate">{p.name}</span>
                <span className="font-mono font-semibold text-red-600">{p['real-stock'] ?? 0} ud.</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b px-5 py-3">
            <h3 className="font-bold text-sm text-gray-700">Entradas/salidas con más movimiento</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {loadingMovimientos && <p className="text-sm text-gray-400 text-center py-6">Cargando...</p>}
            {!loadingMovimientos && kpis.topProductos.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Sin movimientos en este periodo</p>}
            {!loadingMovimientos && kpis.topProductos.map(p => (
              <div key={p.productoId} className="flex items-center justify-between px-5 py-2.5 text-sm">
                <span className="text-gray-700 truncate">{p.nombre}</span>
                <span className="font-mono font-semibold text-gray-900">{p.count} mov.</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'productos',   label: 'Productos' },
  { id: 'servicios',   label: 'Servicios' },
  { id: 'movimientos', label: 'Movimientos' },
  { id: 'solicitudes', label: 'Solicitudes' },
  { id: 'analitica',   label: 'Analítica' },
];

export default function AlmacenView() {
  const [tab, setTab] = useState('productos');
  // `productos` NO se centraliza aquí — cada pestaña que lo necesita hace su
  // propia consulta acotada (ver ProductosTab, AjusteModal, AnaliticaTab):
  // el catálogo completo de StelOrder son ~22.700 productos reales
  // (comprobado en vivo el 2026-09-18), y cargarlo entero en un solo sitio
  // era justo el problema (además de superar el límite de 10.000 documentos
  // por consulta que impone Firestore). `productosVisibles` es solo lo que
  // ProductosTab tiene cargado en cada momento, para la lista compacta de la
  // ficha de detalle — no el catálogo entero.
  const [productosVisibles, setProductosVisibles] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);
  const [modalAjuste, setModalAjuste] = useState(null); // null | {} (sin producto, elegir) | {producto}
  const [prefiltroAlertas, setPrefiltroAlertas] = useState(false); // drill-down desde Analítica
  const [movFiltroAnalitica] = useState('mes');
  const [movimientosAnalitica, setMovimientosAnalitica] = useState([]);
  const [loadingMovAnalitica, setLoadingMovAnalitica] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // "🔄 Actualizar" remonta la pestaña activa
  const [generandoCatalogo, setGenerandoCatalogo] = useState(false);
  const [msgCatalogo, setMsgCatalogo] = useState('');
  const esAdmin = authService.getCurrentUser()?.role === 'admin';

  // Catálogo offline de técnicos (ver catalogoLocal.js) — se regenera solo de
  // madrugada; este botón es para no esperar a esa hora (p. ej. recién
  // desplegado el feature, o tras un cambio grande de precios/catálogo).
  const handleGenerarCatalogo = async () => {
    setGenerandoCatalogo(true); setMsgCatalogo('');
    try {
      const res = await fetch('/.netlify/functions/forzarGenerarCatalogoProductos', { method: 'POST', headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setMsgCatalogo(`Catálogo regenerado: ${data.count} productos (${(data.bytes / 1024).toFixed(0)} KB).`);
    } catch (e) {
      setMsgCatalogo('Error: ' + e.message);
    } finally {
      setGenerandoCatalogo(false);
    }
  };

  // Servicios — 100% catálogo StelOrder importado (no hay alta manual), solo
  // consulta. Colección separada, sin relación con `productos`.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'servicios'), limit(5000)),
      snap => {
        setServicios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingServicios(false);
      },
      () => setLoadingServicios(false)
    );
    return unsub;
  }, []);

  // Movimientos del mes para Analítica — periodo fijo, independiente del
  // filtro que el usuario elija dentro de la pestaña Movimientos.
  useEffect(() => {
    const rango = rangoFechasMov(movFiltroAnalitica);
    setLoadingMovAnalitica(true);
    getDocs(query(collection(db, 'movimientos_stock'),
      where('fecha', '>=', Timestamp.fromDate(rango.desde)), where('fecha', '<', Timestamp.fromDate(rango.hasta)),
      orderBy('fecha', 'desc'), limit(1000)))
      .then(snap => setMovimientosAnalitica(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setMovimientosAnalitica([]))
      .finally(() => setLoadingMovAnalitica(false));
  }, [movFiltroAnalitica]);

  useEffect(() => {
    // Al cambiar de pestaña se cierra la ficha de detalle — mismo motivo que
    // en OrdenesView.js: no tiene sentido dejarla "pegada" en otra pestaña.
    setSeleccionado(null);
  }, [tab]);

  // Cualquier cambio de pestaña pasa por aquí, para que `prefiltroAlertas` se
  // reponga a false salvo que sea justo el drill-down desde Analítica — sin
  // esto, tras un clic en "Stock bajo" el filtro se quedaría "pegado" cada
  // vez que se vuelve a Productos por la barra de pestañas normal.
  const goToTab = (id, opts = {}) => {
    setPrefiltroAlertas(!!opts.soloAlertas);
    setTab(id);
  };

  const handleAjuste = useCallback(async (producto, { tipo, cantidad, notas }) => {
    const stockActual = producto['real-stock'] || 0;
    let delta = tipo === 'entrada' ? cantidad : tipo === 'salida' ? -cantidad : null;
    const stockNuevo = tipo === 'ajuste' ? cantidad : stockActual + delta;

    if (tipo === 'ajuste') {
      await updateDoc(doc(db, 'productos', producto.id), { 'real-stock': cantidad });
    } else {
      await updateDoc(doc(db, 'productos', producto.id), { 'real-stock': increment(delta) });
    }

    await addDoc(collection(db, 'movimientos_stock'), {
      productoId: producto.id, productoNombre: producto.name, tipo, cantidad,
      stockAnterior: stockActual, stockNuevo, notas: notas || '', fecha: serverTimestamp(),
    });

    setProductosVisibles(prev => prev.map(p => (p.id === producto.id ? { ...p, 'real-stock': stockNuevo } : p)));
    if (seleccionado?.id === producto.id) setSeleccionado(s => ({ ...s, 'real-stock': stockNuevo }));
  }, [seleccionado]);

  // Edición inline en la ficha (ProductoDetallePanel) — sin catálogo entero
  // en memoria, solo se refleja en `seleccionado` y en lo que ProductosTab
  // tenga cargado ahora mismo (`productosVisibles`).
  const handleUpdateProducto = useCallback((actualizado) => {
    setSeleccionado(actualizado);
    setProductosVisibles(prev => prev.map(p => (p.id === actualizado.id ? actualizado : p)));
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] gap-3">
      <div className="flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-900">Almacén</h1>
        <div className="flex items-center gap-2">
          {msgCatalogo && <span className="text-xs font-semibold text-gray-500">{msgCatalogo}</span>}
          {esAdmin && (
            <button onClick={handleGenerarCatalogo} disabled={generandoCatalogo}
              title="Regenera ya el catálogo offline que usan los técnicos, sin esperar al cron de madrugada"
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition-colors">
              {generandoCatalogo ? 'Generando…' : '📱 Regenerar catálogo técnicos'}
            </button>
          )}
          <button onClick={() => setRefreshKey(k => k + 1)}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
            🔄 Actualizar
          </button>
        </div>
      </div>

      <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1 w-fit flex-shrink-0">
        {TABS.map(t => (
          <button key={t.id} onClick={() => goToTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${tab === t.id ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0">
        {tab === 'productos' ? (
          seleccionado ? (
            <ProductoDetallePanel
              producto={seleccionado}
              lista={productosVisibles.length ? productosVisibles : [seleccionado]}
              onSeleccionar={setSeleccionado}
              onClose={() => setSeleccionado(null)}
              onUpdate={handleUpdateProducto}
              onAjustar={p => setModalAjuste({ producto: p })}
            />
          ) : (
            <ProductosTab
              key={`productos-${refreshKey}`}
              onDetalle={setSeleccionado}
              onAjustar={p => setModalAjuste({ producto: p })}
              soloAlertasInicial={prefiltroAlertas}
              onListaChange={setProductosVisibles}
            />
          )
        ) : tab === 'servicios' ? (
          <ServiciosTab servicios={servicios} loading={loadingServicios} />
        ) : tab === 'movimientos' ? (
          <MovimientosTab key={`movimientos-${refreshKey}`} onRegistrar={() => setModalAjuste({})} />
        ) : tab === 'solicitudes' ? (
          <div className="h-full overflow-y-auto"><SolicitudesMaterial /></div>
        ) : (
          <AnaliticaTab
            key={`analitica-${refreshKey}`}
            movimientos={movimientosAnalitica}
            loadingMovimientos={loadingMovAnalitica}
            onIrAProductos={() => goToTab('productos', { soloAlertas: true })}
          />
        )}
      </div>

      {modalAjuste && (
        <AjusteModal
          productoInicial={modalAjuste.producto || null}
          onClose={() => setModalAjuste(null)}
          onAjuste={handleAjuste}
        />
      )}
    </div>
  );
}
